import React, { useEffect } from 'react';
import { AdvertorialHeader } from '../components/AdvertorialHeader';
import { AdvertorialFooter } from '../components/AdvertorialFooter';
import { AdvertorialSidebar } from '../components/AdvertorialSidebar';
import { TestimonialCard } from '../components/TestimonialCard';
import { ArrowRight, CheckCircle2, AlertCircle, Star } from 'lucide-react';
export function AdvertorialPage() {
  // Set page title
  useEffect(() => {
    document.title = 'Barkvee • Advertorial';
  }, []);
  return <div className="min-h-screen bg-white flex flex-col font-sans" data-id="element-94">
      <AdvertorialHeader data-id="element-95" />

      <main className="flex-grow" data-id="element-96">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12" data-id="element-97">
          <div className="flex flex-col lg:flex-row gap-12" data-id="element-98">
            {/* Main Content Column */}
            <article className="lg:w-2/3 max-w-3xl" data-id="element-99">
              {/* Headline Section */}
              <div className="mb-8 border-b border-gray-200 pb-8" data-id="element-100">
                <h1 className="font-sans font-extrabold text-3xl md:text-4xl lg:text-5xl text-gray-900 leading-tight mb-4" data-id="element-101">
                  Why Your Knee Pain Keeps Coming Back{' '}
                  <br className="hidden md:block" data-id="element-102" />
                  <span className="text-[#2B7FD9]" data-id="element-103">
                    (And a <mark data-id="element-104">Simple Option</mark> Doctors Don't Always Talk
                    About)
                  </span>
                </h1>

                <div className="flex items-center gap-4 text-sm text-gray-500 mt-6" data-id="element-105">
                  <div className="flex items-center gap-2" data-id="element-106">
                    <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden" data-id="element-107">
                      <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=100" alt="Sandra Mitchell" className="w-full h-full object-cover" data-id="element-108" />
                    </div>
                    <div data-id="element-109">
                      <p className="font-bold text-gray-900" data-id="element-110">
                        By Sandra Mitchell
                      </p>
                      <p data-id="element-111">Health & Mobility Editor</p>
                    </div>
                  </div>
                  <div className="h-8 w-px bg-gray-300 mx-2" data-id="element-112"></div>
                  <p data-id="element-113">Updated: January 2026</p>
                </div>
              </div>

              {/* Article Body */}
              <div className="prose prose-xl prose-slate max-w-none font-serif text-gray-800" data-id="element-114">
                <p className="lead text-2xl md:text-3xl leading-relaxed font-medium text-gray-700 mb-8" data-id="element-115">
                  If you're over 55 and dealing with chronic knee pain, there's
                  something I think you deserve to hear.
                </p>

                <p className="text-xl leading-relaxed" data-id="element-116">
                  You've probably been told — by well-meaning doctors, by
                  family, maybe even by yourself — that your knee pain is "just
                  part of getting older."
                </p>

                <p className="text-xl leading-relaxed" data-id="element-117">
                  That there's nothing to do but manage it with medication, ice
                  it when it flares up, and eventually... consider the surgery
                  you've been hoping to avoid.
                </p>

                <p className="font-bold text-gray-900 text-xl leading-relaxed" data-id="element-118">
                  But here's something worth considering: what if pain doesn't
                  have to define this chapter of your life?
                </p>

                <div className="my-8 rounded-xl overflow-hidden shadow-lg" data-id="element-119">
                  <img src="https://cdn.magicpatterns.com/uploads/fmB8mQGutBVefQDpNHUMwj/old_man_in_grocery_store.png" alt="Senior man walking comfortably in grocery store" className="w-full h-auto object-cover" data-id="element-120" />
                </div>

                <p data-id="element-121">
                  What if you could still play with your grandkids? Walk through
                  the grocery store without bracing for pain? Get out of a chair
                  without that look from your family — the one that says, "Mom's
                  slowing down."
                </p>

                <p data-id="element-122">
                  What if the reason nothing has fully worked isn't because your
                  knee is beyond help — but because what you've tried only
                  addressed part of the picture?
                </p>

                <h2 className="font-sans font-bold text-2xl md:text-3xl text-gray-900 mt-12 mb-6" data-id="element-123">
                  Why So Many People Feel Stuck
                </h2>

                <p data-id="element-124">
                  Many adults over 55 have been told that knee pain is just
                  something you learn to live with as you age.
                </p>

                <p data-id="element-125">
                  And it's easy to understand why people believe that. Doctors
                  are busy. Appointments are short. And when you're told "it's
                  arthritis" or "it's wear and tear," it can feel like a door
                  closing.
                </p>

                <div className="bg-blue-50 border-l-4 border-[#2B7FD9] p-6 my-8 rounded-r-lg" data-id="element-126">
                  <p className="font-bold text-[#2B7FD9] mb-2 font-sans uppercase tracking-wide text-sm" data-id="element-127">
                    Key Insight
                  </p>
                  <p className="italic text-lg text-gray-800 m-0" data-id="element-128">
                    "Knee discomfort often involves a combination of factors —
                    reduced circulation, inflammation, stiffness, and lack of
                    consistent support."
                  </p>
                </div>

                <p data-id="element-129">
                  These aren't mysteries. They're things that can be addressed —
                  especially with the right daily habits.
                </p>

                <p data-id="element-130">
                  The challenge? Most approaches only tackle one piece at a time
                  — and leave the rest unaddressed. That's why relief can feel
                  temporary, even when you're doing "all the right things."
                </p>

                <h2 className="font-sans font-bold text-2xl md:text-3xl text-gray-900 mt-12 mb-6" data-id="element-131">
                  What You've Probably Already Tried
                </h2>

                <p data-id="element-132">
                  If you're like most people I hear from, you haven't been
                  sitting around doing nothing. You've been proactive. You've
                  tried things. Maybe several things.
                </p>

                <ul className="space-y-6 list-none pl-0 my-8" data-id="element-133">
                  <li className="flex gap-4" data-id="element-134">
                    <div className="shrink-0 mt-1" data-id="element-135">
                      <AlertCircle className="w-6 h-6 text-red-500" data-id="element-136" />
                    </div>
                    <div data-id="element-137">
                      <strong className="font-sans text-gray-900 block mb-1" data-id="element-138">
                        Pain medication
                      </strong>
                      For many people, over-the-counter or prescription
                      medication is an important part of managing flare-ups. But
                      some people find that medication alone doesn't address the
                      underlying stiffness or instability.
                    </div>
                  </li>
                  <li className="flex gap-4" data-id="element-139">
                    <div className="shrink-0 mt-1" data-id="element-140">
                      <AlertCircle className="w-6 h-6 text-red-500" data-id="element-141" />
                    </div>
                    <div data-id="element-142">
                      <strong className="font-sans text-gray-900 block mb-1" data-id="element-143">
                        Knee braces and sleeves
                      </strong>
                      Braces can be helpful for stability. But not all braces
                      are created equal. Some slide down. Some are bulky. And
                      while they can provide support, they don't do anything for
                      circulation or stiffness.
                    </div>
                  </li>
                  <li className="flex gap-4" data-id="element-144">
                    <div className="shrink-0 mt-1" data-id="element-145">
                      <AlertCircle className="w-6 h-6 text-red-500" data-id="element-146" />
                    </div>
                    <div data-id="element-147">
                      <strong className="font-sans text-gray-900 block mb-1" data-id="element-148">
                        Heating pads and ice packs
                      </strong>
                      Heat can feel wonderful. The challenge with traditional
                      heating pads is that they require you to sit still for
                      15-20 minutes. The moment you take it off and start
                      moving? The stiffness often creeps back.
                    </div>
                  </li>
                </ul>

                <h2 className="font-sans font-bold text-2xl md:text-3xl text-gray-900 mt-12 mb-6" data-id="element-149">
                  What's Often Missing From the Conversation
                </h2>

                <p data-id="element-150">
                  Here's what I've noticed after talking to hundreds of people
                  dealing with knee discomfort:{' '}
                  <mark data-id="element-151">Most solutions focus on just one thing at a time.</mark>
                </p>

                <p data-id="element-152">
                  What if you could address multiple factors at once — warmth
                  for circulation and comfort, gentle compression for support
                  and stability — in something you could actually wear while
                  going about your day?
                </p>

                <p data-id="element-153">
                  Think of it like a stiff hinge. You wouldn't just oil it OR
                  hold it steady — ideally, you'd do both together to help it
                  move smoothly again.
                </p>

                <h2 className="font-sans font-bold text-2xl md:text-3xl text-gray-900 mt-12 mb-6" data-id="element-154">
                  A Daily Comfort Option Worth Knowing About
                </h2>

                <div className="float-right ml-6 mb-6 w-1/2 max-w-xs" data-id="element-155">
                  <img src="https://cdn.magicpatterns.com/uploads/8Fpm8aBwNgnBDDxSuvjmcS/heat_and_compression_knee_pad.png" alt="Therma-Compression Therapy Wrap" className="rounded-lg shadow-md w-full border border-gray-200" data-id="element-156" />
                </div>

                <p data-id="element-157">
                  This is why more and more people are turning to what's
                  sometimes called <strong data-id="element-158">Therma-Compression Therapy</strong> —
                  a combination of gentle heat and supportive compression in a
                  wearable wrap.
                </p>

                <p data-id="element-159">
                  It's not a replacement for medical care. It's not a miracle
                  cure. It's simply a daily comfort option you can use at home,
                  on your own schedule.
                </p>

                <h3 className="font-sans font-bold text-xl text-gray-900 mt-8 mb-4" data-id="element-160">
                  Here's how it works:
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 my-8 not-prose" data-id="element-161">
                  <div className="bg-blue-50 p-4 rounded-lg text-center" data-id="element-162">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3 text-blue-600" data-id="element-163">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-164">
                        <path d="M12 9a4 4 0 0 0-2 7.5" data-id="element-165" />
                        <path d="M12 3v2" data-id="element-166" />
                        <path d="M6.6 18.4l-1.4 1.4" data-id="element-167" />
                        <path d="M20 4v10.54a4 4 0 1 1-4 0V4a2 2 0 0 1 4 0Z" data-id="element-168" />
                      </svg>
                    </div>
                    <h4 className="font-sans font-bold text-gray-900 mb-2" data-id="element-169">
                      Gentle Warmth
                    </h4>
                    <p className="text-sm text-gray-700 font-sans" data-id="element-170">
                      Soothing heat wraps around the knee to ease morning
                      stiffness and encourage blood flow.
                    </p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg text-center" data-id="element-171">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3 text-blue-600" data-id="element-172">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-173">
                        <path d="M21 11V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h6" data-id="element-174" />
                        <path d="m12 12 4 10 1.7-4.3L22 16Z" data-id="element-175" />
                      </svg>
                    </div>
                    <h4 className="font-sans font-bold text-gray-900 mb-2" data-id="element-176">
                      Supportive Compression
                    </h4>
                    <p className="text-sm text-gray-700 font-sans" data-id="element-177">
                      Adjustable compression provides a secure "hug" that helps
                      you feel stable and confident.
                    </p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg text-center" data-id="element-178">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3 text-blue-600" data-id="element-179">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-180">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" data-id="element-181" />
                        <path d="m9 12 2 2 4-4" data-id="element-182" />
                      </svg>
                    </div>
                    <h4 className="font-sans font-bold text-gray-900 mb-2" data-id="element-183">
                      Designed for Real Life
                    </h4>
                    <p className="text-sm text-gray-700 font-sans" data-id="element-184">
                      Cordless and lightweight. Use it while watching TV, doing
                      dishes, or sitting in the car.
                    </p>
                  </div>
                </div>

                <h2 className="font-sans font-bold text-2xl md:text-3xl text-gray-900 mt-12 mb-6" data-id="element-185">
                  Why People Say This Feels Different
                </h2>

                <p data-id="element-186">
                  I'll be honest — if you've tried other products that didn't
                  work, you have every reason to be skeptical. But here's what
                  we hear most often from people who try this:
                </p>

                <div className="space-y-4 my-6" data-id="element-187">
                  <div className="flex gap-3" data-id="element-188">
                    <CheckCircle2 className="w-6 h-6 text-[#28A745] shrink-0 mt-1" data-id="element-189" />
                    <p className="m-0" data-id="element-190">
                      <strong data-id="element-191">"It actually does more than one thing."</strong>{' '}
                      The combination of heat AND support feels more complete.
                    </p>
                  </div>
                  <div className="flex gap-3" data-id="element-192">
                    <CheckCircle2 className="w-6 h-6 text-[#28A745] shrink-0 mt-1" data-id="element-193" />
                    <p className="m-0" data-id="element-194">
                      <strong data-id="element-195">"I can actually use it."</strong> No complicated
                      setup. No tiny buckles. One button. Simple straps.
                    </p>
                  </div>
                  <div className="flex gap-3" data-id="element-196">
                    <CheckCircle2 className="w-6 h-6 text-[#28A745] shrink-0 mt-1" data-id="element-197" />
                    <p className="m-0" data-id="element-198">
                      <strong data-id="element-199">"It doesn't keep me stuck in one place."</strong>{' '}
                      Because it's cordless, it goes with you.
                    </p>
                  </div>
                </div>

                <h2 className="font-sans font-bold text-2xl md:text-3xl text-gray-900 mt-12 mb-6" data-id="element-200">
                  What Real Users Are Saying
                </h2>

                <div className="not-prose" data-id="element-201">
                  <TestimonialCard name="Sarah T." age={62} quote="I didn't expect much, honestly. I've bought things before that ended up in a drawer. But this one I actually use. It's easy to put on, and my knee just feels better when I wear it. Not a miracle — but a real difference I notice." imageUrl="https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=100&h=100" data-id="element-202" />
                  <TestimonialCard name="Robert K." age={67} quote="I was skeptical because I'd tried other braces that just sat there. This is different — the warmth actually helps with that morning stiffness. I wear it while I have my coffee and by the time I'm ready to move around, my knee cooperates better." imageUrl="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=100&h=100" data-id="element-203" />
                  <TestimonialCard name="Patricia M." age={71} quote="The thing I like most is that I don't have to ask my daughter to help me with it. I can put it on myself, use it when I want, and take it off when I'm done. At 71, that independence matters to me more than I can say." imageUrl="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=100&h=100" data-id="element-204" />
                </div>

                <h2 className="font-sans font-bold text-2xl md:text-3xl text-gray-900 mt-12 mb-6" data-id="element-205">
                  Try It and See How It Feels
                </h2>

                <p data-id="element-206">
                  We understand that after trying things that didn't work, it's
                  hard to take another chance. That's why we offer a{' '}
                  <strong data-id="element-207">60-day satisfaction guarantee</strong>.
                </p>

                <p data-id="element-208">
                  Use it for a full two months. See if it helps with your
                  morning stiffness. Notice whether you feel more comfortable
                  during your daily activities.
                </p>

                <p data-id="element-209">
                  If it's not right for you, simply return it for a refund. No
                  complicated process. No guilt.
                </p>

                <div className="bg-gray-100 p-8 rounded-2xl my-12 text-center border-2 border-dashed border-gray-300" data-id="element-210">
                  <h3 className="font-sans font-bold text-2xl text-gray-900 mb-4" data-id="element-211">
                    You Deserve to Feel More Comfortable
                  </h3>
                  <p className="text-lg mb-8 max-w-2xl mx-auto" data-id="element-212">
                    Thousands of people are using this daily to feel a little
                    more like themselves — more comfortable, more mobile, more
                    confident. Maybe it's worth seeing if it could help you too.
                  </p>

                  <a href="https://barkvee.com/products/knee-comfort-pad" target="_blank" rel="noopener noreferrer" className="w-full md:w-auto bg-[#28A745] hover:bg-[#218838] text-white font-bold text-xl py-4 px-12 rounded-full shadow-lg transition-all transform hover:scale-105 flex items-center justify-center gap-2 mx-auto" data-id="element-213">
                    Check Availability Now
                    <ArrowRight className="w-6 h-6" data-id="element-214" />
                  </a>

                  <div className="mt-6 flex flex-col md:flex-row items-center justify-center gap-4 text-sm text-gray-600 font-sans" data-id="element-215">
                    <span className="flex items-center gap-1" data-id="element-216">
                      <CheckCircle2 className="w-4 h-4 text-[#28A745]" data-id="element-217" /> 60-Day
                      Satisfaction Guarantee
                    </span>
                    <span className="hidden md:inline" data-id="element-218">•</span>
                    <span className="flex items-center gap-1" data-id="element-219">
                      <CheckCircle2 className="w-4 h-4 text-[#28A745]" data-id="element-220" /> Free
                      Shipping
                    </span>
                    <span className="hidden md:inline" data-id="element-221">•</span>
                    <span className="flex items-center gap-1" data-id="element-222">
                      <Star className="w-4 h-4 text-[#FFD700] fill-[#FFD700]" data-id="element-223" />{' '}
                      4.8/5 Customer Rating
                    </span>
                  </div>
                </div>
              </div>
            </article>

            {/* Sidebar Column */}
            <aside className="lg:w-1/3" data-id="element-224">
              <AdvertorialSidebar data-id="element-225" />
            </aside>
          </div>
        </div>
      </main>

      <AdvertorialFooter data-id="element-226" />
    </div>;
}